/**
 * OCR Context Service - extracts names/places from journal entries to improve OCR accuracy
 */

import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { db, COLLECTIONS } from '@/shared/utils/core/firebase-config';
import logger from '@/shared/utils/core/logger';

/**
 * Cached context for a user
 */
export interface UserOCRContext {
  userId: string;
  names: string[];       // People, pets, etc.
  places: string[];      // Locations, addresses
  terms: string[];       // Frequently used words/phrases
  lastUpdated: Timestamp | null;
  entryCount: number;    // Number of entries analyzed
  version: string;       // Cache version for invalidation
}

/**
 * Simple named entity patterns
 */
const NAME_PATTERNS = {
  // Common name prefixes
  prefixes: /\b(Mr\.|Mrs\.|Ms\.|Dr\.|Prof\.|Uncle|Aunt|Grandma|Grandpa|Mom|Dad|Brother|Sister)\s+([A-Z][a-z]+)/g,
  // Capitalized words that might be names (2+ letters)
  capitalizedWords: /\b[A-Z][a-z]{2,}/g,
  // Place indicators - stricter pattern requiring the/a before place or common place suffixes
  placeIndicators: /\b(?:at|in|to|from|near|visited|went to|traveled to|arrived at|left)\s+(?:the\s+)?([A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,3})/gi,
};

/**
 * Common place suffixes that indicate actual geographic locations
 */
const PLACE_SUFFIXES = new Set([
  'City', 'Town', 'Village', 'County', 'State', 'Country',
  'Park', 'Beach', 'Lake', 'River', 'Mountain', 'Hill', 'Valley', 'Island',
  'Street', 'Avenue', 'Road', 'Drive', 'Boulevard', 'Lane', 'Way', 'Place',
  'Square', 'Plaza', 'Center', 'Centre', 'Mall', 'Market',
  'Station', 'Airport', 'Harbor', 'Port',
  'Hospital', 'School', 'University', 'College', 'Academy',
  'Hotel', 'Resort', 'Inn', 'Lodge',
  'Restaurant', 'Cafe', 'Diner', 'Grill', 'Bar', 'Pub',
  'Church', 'Temple', 'Mosque', 'Cathedral', 'Chapel',
  'Museum', 'Gallery', 'Theater', 'Theatre', 'Stadium', 'Arena',
  'Store', 'Shop', 'Mart', 'Market',
  'Building', 'Tower', 'House', 'Hall', 'Room',
  'Garden', 'Gardens', 'Woods', 'Forest', 'Grove',
  'Heights', 'Point', 'Bay', 'Cove', 'Creek',
]);

/**
 * Words that should NEVER be extracted as places
 */
const PLACE_EXCLUDE_WORDS = new Set([
  // Common words that follow prepositions but aren't places
  'Your', 'My', 'His', 'Her', 'Their', 'Our', 'Its',
  'The', 'This', 'That', 'These', 'Those',
  'Head', 'Mind', 'Heart', 'Life', 'Time', 'Way', 'Place',
  'Moment', 'Fact', 'Truth', 'Point', 'End', 'Start',
  'Morning', 'Afternoon', 'Evening', 'Night', 'Today', 'Yesterday',
  'Order', 'Control', 'Touch', 'Reach', 'Sight', 'View',
  'General', 'Particular', 'Essence', 'Spite', 'Addition',
  'Response', 'Regard', 'Return', 'Terms', 'Case', 'Line',
  'Advance', 'Charge', 'Common', 'Favor', 'Front', 'Light',
  'Love', 'Need', 'Pain', 'Peace', 'Person', 'Trouble',
  'Shine', 'Shadow', 'Dark', 'Darkness', 'Light',
  'Work', 'School', 'Class', 'Bed', 'Sleep', 'Rest',
  'Top', 'Bottom', 'Middle', 'Center', 'Side', 'Back', 'Front',
  'First', 'Last', 'Second', 'Third', 'One', 'Two', 'Three',
  'All', 'Any', 'Some', 'Many', 'Most', 'Part', 'Half',
  'Whole', 'Same', 'Other', 'Another', 'Such',
  'Real', 'Full', 'Short', 'Long', 'Good', 'Bad', 'Best', 'Worst',
  'New', 'Old', 'Young', 'High', 'Low', 'Deep',
]);

/**
 * Common lowercase terms to exclude from term extraction
 * These are too generic to be useful for OCR context
 */
const COMMON_TERM_EXCLUSIONS = new Set([
  // Very common words
  'about', 'above', 'after', 'again', 'against', 'along', 'always',
  'another', 'around', 'because', 'become', 'before', 'being', 'below',
  'between', 'both', 'bring', 'could', 'doing', 'during', 'each',
  'either', 'enough', 'every', 'everything', 'found', 'getting',
  'going', 'great', 'having', 'however', 'inside', 'into', 'itself',
  'just', 'keep', 'known', 'later', 'least', 'leave', 'less',
  'like', 'little', 'long', 'look', 'made', 'make', 'many', 'might',
  'more', 'most', 'much', 'must', 'myself', 'need', 'never', 'nothing',
  'only', 'other', 'others', 'over', 'part', 'perhaps', 'place',
  'point', 'probably', 'quite', 'rather', 'really', 'right', 'same',
  'seem', 'should', 'since', 'small', 'some', 'something', 'still',
  'such', 'sure', 'take', 'than', 'that', 'their', 'them', 'then',
  'there', 'these', 'they', 'thing', 'things', 'think', 'this',
  'those', 'though', 'through', 'time', 'today', 'together', 'under',
  'until', 'upon', 'very', 'want', 'well', 'went', 'were', 'what',
  'when', 'where', 'which', 'while', 'whole', 'will', 'with',
  'within', 'without', 'would', 'your', 'yourself',
  // Journal-specific common words
  'feeling', 'feelings', 'felt', 'morning', 'evening', 'night',
  'today', 'yesterday', 'tomorrow', 'week', 'month', 'year',
  'journal', 'entry', 'write', 'writing', 'wrote', 'thought',
  'thoughts', 'thinking', 'grateful', 'gratitude', 'reflect',
  'reflection', 'moment', 'moments', 'life', 'day', 'days',
]);

/** Words to exclude from extraction (common words often capitalized) */
const EXCLUDE_WORDS = new Set([
  // Pronouns & determiners
  'I', 'Me', 'My', 'Mine', 'Myself',
  'You', 'Your', 'Yours', 'Yourself', 'Yourselves',
  'He', 'Him', 'His', 'Himself',
  'She', 'Her', 'Hers', 'Herself',
  'It', 'Its', 'Itself',
  'We', 'Us', 'Our', 'Ours', 'Ourselves',
  'They', 'Them', 'Their', 'Theirs', 'Themselves',
  'Who', 'Whom', 'Whose', 'Which', 'What', 'That', 'This',
  'These', 'Those', 'Anyone', 'Everyone', 'Someone', 'Nobody',
  'Anybody', 'Everybody', 'Somebody', 'Nothing', 'Everything',
  'Something', 'Anything', 'Each', 'Either', 'Neither', 'Both',
  'All', 'None', 'One', 'Other', 'Another', 'Such', 'Same',

  // Days, months & time
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
  'Today', 'Yesterday', 'Tomorrow', 'Tonight', 'Everyday',
  'Morning', 'Afternoon', 'Evening', 'Night', 'Midnight', 'Noon',
  'Dawn', 'Dusk', 'Sunrise', 'Sunset',
  'Day', 'Days', 'Week', 'Weeks', 'Weekend', 'Weekday',
  'Month', 'Months', 'Year', 'Years', 'Decade', 'Century',
  'Time', 'Times', 'Hour', 'Hours', 'Minute', 'Minutes',
  'Second', 'Seconds', 'Moment', 'Moments', 'While', 'Period',
  'Season', 'Seasons', 'Spring', 'Summer', 'Fall', 'Autumn', 'Winter',

  // Common verbs
  'Am', 'Is', 'Are', 'Was', 'Were', 'Be', 'Been', 'Being',
  'Have', 'Has', 'Had', 'Having',
  'Do', 'Does', 'Did', 'Done', 'Doing',
  'Will', 'Would', 'Shall', 'Should', 'Can', 'Could', 'May', 'Might', 'Must',
  'Want', 'Wanted', 'Wants', 'Wanting',
  'Need', 'Needed', 'Needs', 'Needing',
  'Like', 'Liked', 'Likes', 'Liking',
  'Love', 'Loved', 'Loves', 'Loving',
  'Hate', 'Hated', 'Hates', 'Hating',
  'Think', 'Thought', 'Thinks', 'Thinking',
  'Know', 'Knew', 'Knows', 'Knowing', 'Known',
  'Feel', 'Felt', 'Feels', 'Feeling',
  'See', 'Saw', 'Sees', 'Seeing', 'Seen',
  'Look', 'Looked', 'Looks', 'Looking',
  'Watch', 'Watched', 'Watches', 'Watching',
  'Hear', 'Heard', 'Hears', 'Hearing',
  'Listen', 'Listened', 'Listens', 'Listening',
  'Find', 'Found', 'Finds', 'Finding',
  'Give', 'Gave', 'Gives', 'Giving', 'Given',
  'Take', 'Took', 'Takes', 'Taking', 'Taken',
  'Make', 'Made', 'Makes', 'Making',
  'Get', 'Got', 'Gets', 'Getting', 'Gotten',
  'Come', 'Came', 'Comes', 'Coming',
  'Go', 'Went', 'Goes', 'Going', 'Gone',
  'Put', 'Puts', 'Putting',
  'Say', 'Said', 'Says', 'Saying',
  'Tell', 'Told', 'Tells', 'Telling',
  'Ask', 'Asked', 'Asks', 'Asking',
  'Try', 'Tried', 'Tries', 'Trying',
  'Keep', 'Kept', 'Keeps', 'Keeping',
  'Let', 'Lets', 'Letting',
  'Begin', 'Began', 'Begins', 'Beginning', 'Begun',
  'Start', 'Started', 'Starts', 'Starting',
  'Stop', 'Stopped', 'Stops', 'Stopping',
  'End', 'Ended', 'Ends', 'Ending',
  'Continue', 'Continued', 'Continues', 'Continuing',
  'Help', 'Helped', 'Helps', 'Helping',
  'Show', 'Showed', 'Shows', 'Showing', 'Shown',
  'Move', 'Moved', 'Moves', 'Moving',
  'Live', 'Lived', 'Lives', 'Living',
  'Work', 'Worked', 'Works', 'Working',
  'Play', 'Played', 'Plays', 'Playing',
  'Run', 'Ran', 'Runs', 'Running',
  'Walk', 'Walked', 'Walks', 'Walking',
  'Talk', 'Talked', 'Talks', 'Talking',
  'Speak', 'Spoke', 'Speaks', 'Speaking', 'Spoken',
  'Read', 'Reads', 'Reading',
  'Write', 'Wrote', 'Writes', 'Writing', 'Written',
  'Learn', 'Learned', 'Learns', 'Learning',
  'Teach', 'Taught', 'Teaches', 'Teaching',
  'Meet', 'Met', 'Meets', 'Meeting',
  'Leave', 'Left', 'Leaves', 'Leaving',
  'Stay', 'Stayed', 'Stays', 'Staying',
  'Return', 'Returned', 'Returns', 'Returning',
  'Wait', 'Waited', 'Waits', 'Waiting',
  'Sit', 'Sat', 'Sits', 'Sitting',
  'Stand', 'Stood', 'Stands', 'Standing',
  'Sleep', 'Slept', 'Sleeps', 'Sleeping',
  'Wake', 'Woke', 'Wakes', 'Waking', 'Woken',
  'Eat', 'Ate', 'Eats', 'Eating', 'Eaten',
  'Drink', 'Drank', 'Drinks', 'Drinking', 'Drunk',
  'Buy', 'Bought', 'Buys', 'Buying',
  'Sell', 'Sold', 'Sells', 'Selling',
  'Pay', 'Paid', 'Pays', 'Paying',
  'Spend', 'Spent', 'Spends', 'Spending',
  'Send', 'Sent', 'Sends', 'Sending',
  'Receive', 'Received', 'Receives', 'Receiving',
  'Call', 'Called', 'Calls', 'Calling',
  'Answer', 'Answered', 'Answers', 'Answering',
  'Decide', 'Decided', 'Decides', 'Deciding',
  'Choose', 'Chose', 'Chooses', 'Choosing', 'Chosen',
  'Remember', 'Remembered', 'Remembers', 'Remembering',
  'Forget', 'Forgot', 'Forgets', 'Forgetting', 'Forgotten',
  'Believe', 'Believed', 'Believes', 'Believing',
  'Understand', 'Understood', 'Understands', 'Understanding',
  'Realize', 'Realized', 'Realizes', 'Realizing',
  'Notice', 'Noticed', 'Notices', 'Noticing',
  'Wonder', 'Wondered', 'Wonders', 'Wondering',
  'Wish', 'Wished', 'Wishes', 'Wishing',
  'Expect', 'Expected', 'Expects', 'Expecting',
  'Seem', 'Seemed', 'Seems', 'Seeming',
  'Appear', 'Appeared', 'Appears', 'Appearing',
  'Become', 'Became', 'Becomes', 'Becoming',
  'Happen', 'Happened', 'Happens', 'Happening',
  'Change', 'Changed', 'Changes', 'Changing',
  'Grow', 'Grew', 'Grows', 'Growing', 'Grown',
  'Turn', 'Turned', 'Turns', 'Turning',
  'Follow', 'Followed', 'Follows', 'Following',
  'Lead', 'Led', 'Leads', 'Leading',
  'Bring', 'Brought', 'Brings', 'Bringing',
  'Carry', 'Carried', 'Carries', 'Carrying',
  'Hold', 'Held', 'Holds', 'Holding',
  'Reach', 'Reached', 'Reaches', 'Reaching',
  'Set', 'Sets', 'Setting',
  'Add', 'Added', 'Adds', 'Adding',
  'Open', 'Opened', 'Opens', 'Opening',
  'Close', 'Closed', 'Closes', 'Closing',
  'Create', 'Created', 'Creates', 'Creating',
  'Build', 'Built', 'Builds', 'Building',
  'Break', 'Broke', 'Breaks', 'Breaking', 'Broken',
  'Fix', 'Fixed', 'Fixes', 'Fixing',
  'Use', 'Used', 'Uses', 'Using',
  'Include', 'Included', 'Includes', 'Including',
  'Involve', 'Involved', 'Involves', 'Involving',
  'Require', 'Required', 'Requires', 'Requiring',
  'Allow', 'Allowed', 'Allows', 'Allowing',
  'Consider', 'Considered', 'Considers', 'Considering',
  'Suggest', 'Suggested', 'Suggests', 'Suggesting',
  'Offer', 'Offered', 'Offers', 'Offering',
  'Provide', 'Provided', 'Provides', 'Providing',
  'Share', 'Shared', 'Shares', 'Sharing',
  'Support', 'Supported', 'Supports', 'Supporting',
  'Accept', 'Accepted', 'Accepts', 'Accepting',
  'Agree', 'Agreed', 'Agrees', 'Agreeing',
  'Enjoy', 'Enjoyed', 'Enjoys', 'Enjoying',
  'Appreciate', 'Appreciated', 'Appreciates', 'Appreciating',

  // Adjectives & descriptors
  'Good', 'Better', 'Best', 'Bad', 'Worse', 'Worst',
  'Great', 'Greater', 'Greatest', 'Fine', 'Nice', 'Lovely',
  'Beautiful', 'Pretty', 'Handsome', 'Cute', 'Ugly',
  'Big', 'Bigger', 'Biggest', 'Large', 'Larger', 'Largest',
  'Small', 'Smaller', 'Smallest', 'Little', 'Tiny', 'Huge', 'Giant',
  'Long', 'Longer', 'Longest', 'Short', 'Shorter', 'Shortest',
  'Tall', 'Taller', 'Tallest', 'Wide', 'Wider', 'Widest',
  'High', 'Higher', 'Highest', 'Low', 'Lower', 'Lowest',
  'Deep', 'Deeper', 'Deepest', 'Shallow',
  'Fast', 'Faster', 'Fastest', 'Slow', 'Slower', 'Slowest', 'Quick',
  'Hard', 'Harder', 'Hardest', 'Soft', 'Softer', 'Softest',
  'Easy', 'Easier', 'Easiest', 'Difficult', 'Simple', 'Complex',
  'New', 'Newer', 'Newest', 'Old', 'Older', 'Oldest', 'Young', 'Ancient',
  'Hot', 'Hotter', 'Hottest', 'Cold', 'Colder', 'Coldest', 'Warm', 'Cool',
  'Bright', 'Brighter', 'Brightest', 'Dark', 'Darker', 'Darkest', 'Light',
  'Heavy', 'Heavier', 'Heaviest', 'Lightweight',
  'Full', 'Fuller', 'Fullest', 'Empty',
  'Rich', 'Richer', 'Richest', 'Poor', 'Poorer', 'Poorest',
  'Strong', 'Stronger', 'Strongest', 'Weak', 'Weaker', 'Weakest',
  'Clean', 'Cleaner', 'Cleanest', 'Dirty', 'Dirtier', 'Dirtiest',
  'Safe', 'Safer', 'Safest', 'Dangerous',
  'Healthy', 'Healthier', 'Healthiest', 'Sick', 'Ill',
  'Happy', 'Happier', 'Happiest', 'Sad', 'Sadder', 'Saddest',
  'Angry', 'Angrier', 'Angriest', 'Mad', 'Upset', 'Furious',
  'Calm', 'Calmer', 'Calmest', 'Peaceful', 'Relaxed', 'Tense',
  'Anxious', 'Nervous', 'Worried', 'Scared', 'Afraid', 'Fearful',
  'Excited', 'Thrilled', 'Eager', 'Enthusiastic',
  'Tired', 'Exhausted', 'Sleepy', 'Awake', 'Alert', 'Energetic',
  'Busy', 'Busier', 'Busiest', 'Free',
  'Grateful', 'Thankful', 'Blessed', 'Lucky', 'Fortunate',
  'Compassionate', 'Caring', 'Loving', 'Kind', 'Gentle', 'Sweet',
  'Friendly', 'Nice', 'Mean', 'Cruel', 'Harsh',
  'Proud', 'Humble', 'Confident', 'Shy', 'Bold', 'Brave', 'Courageous',
  'Honest', 'Truthful', 'Faithful', 'Loyal', 'Trustworthy',
  'Smart', 'Intelligent', 'Clever', 'Wise', 'Dumb', 'Stupid', 'Foolish',
  'Funny', 'Hilarious', 'Serious', 'Silly',
  'Strange', 'Weird', 'Odd', 'Normal', 'Usual', 'Unusual', 'Common', 'Rare',
  'Special', 'Unique', 'Different', 'Similar', 'Same',
  'Important', 'Significant', 'Major', 'Minor', 'Main', 'Primary',
  'Real', 'True', 'False', 'Fake', 'Genuine', 'Authentic',
  'Perfect', 'Ideal', 'Complete', 'Whole', 'Partial',
  'Possible', 'Impossible', 'Probable', 'Likely', 'Unlikely',
  'Certain', 'Sure', 'Uncertain', 'Unsure', 'Definite',
  'Clear', 'Clearer', 'Clearest', 'Obvious', 'Apparent',
  'Positive', 'Negative', 'Neutral',
  'Right', 'Wrong', 'Correct', 'Incorrect',
  'Alone', 'Together', 'Separate', 'Apart',
  'Ready', 'Prepared', 'Available', 'Unavailable',
  'Open', 'Closed', 'Shut',
  'Alive', 'Dead', 'Living',
  'Awake', 'Asleep',

  // Adverbs
  'Very', 'Really', 'Truly', 'Quite', 'Rather', 'Pretty', 'Fairly',
  'Extremely', 'Incredibly', 'Absolutely', 'Completely', 'Totally', 'Entirely',
  'Almost', 'Nearly', 'Hardly', 'Barely', 'Scarcely',
  'Always', 'Never', 'Sometimes', 'Often', 'Usually', 'Rarely', 'Seldom',
  'Frequently', 'Occasionally', 'Constantly', 'Continuously',
  'Now', 'Then', 'Soon', 'Later', 'Already', 'Still', 'Yet',
  'Just', 'Only', 'Even', 'Also', 'Too', 'Either', 'Neither',
  'Here', 'There', 'Everywhere', 'Somewhere', 'Nowhere', 'Anywhere',
  'Inside', 'Outside', 'Upstairs', 'Downstairs', 'Nearby',
  'Away', 'Back', 'Forward', 'Backward', 'Ahead', 'Behind',
  'Up', 'Down', 'Out', 'In', 'Off', 'On',
  'Together', 'Apart', 'Alone',
  'Maybe', 'Perhaps', 'Probably', 'Possibly', 'Certainly', 'Definitely',
  'Hopefully', 'Fortunately', 'Unfortunately', 'Luckily',
  'Suddenly', 'Gradually', 'Slowly', 'Quickly', 'Fast',
  'Carefully', 'Quietly', 'Loudly', 'Softly', 'Gently',
  'Easily', 'Hardly', 'Simply', 'Directly', 'Exactly',
  'Finally', 'Eventually', 'Ultimately', 'Initially', 'Originally',
  'Mainly', 'Mostly', 'Largely', 'Partly', 'Partially',
  'Actually', 'Basically', 'Generally', 'Normally', 'Typically',
  'Especially', 'Particularly', 'Specifically',
  'Anyway', 'However', 'Therefore', 'Thus', 'Hence',
  'Instead', 'Otherwise', 'Besides', 'Moreover', 'Furthermore',

  // Conjunctions & prepositions
  'And', 'But', 'Or', 'Nor', 'So', 'Yet', 'For',
  'Because', 'Since', 'Although', 'Though', 'While', 'Whereas',
  'If', 'Unless', 'Until', 'When', 'Whenever', 'Where', 'Wherever',
  'Whether', 'Before', 'After', 'As', 'Than',
  'About', 'Above', 'Across', 'After', 'Against', 'Along', 'Among',
  'Around', 'At', 'Before', 'Behind', 'Below', 'Beneath', 'Beside',
  'Besides', 'Between', 'Beyond', 'By', 'Down', 'During', 'Except',
  'For', 'From', 'In', 'Inside', 'Into', 'Like', 'Near', 'Of', 'Off',
  'On', 'Onto', 'Out', 'Outside', 'Over', 'Past', 'Since', 'Through',
  'Throughout', 'Till', 'To', 'Toward', 'Towards', 'Under', 'Underneath',
  'Until', 'Up', 'Upon', 'With', 'Within', 'Without',

  // Common nouns (people, places, things, concepts)
  'Person', 'Persons', 'People', 'Man', 'Men', 'Woman', 'Women',
  'Child', 'Children', 'Kid', 'Kids', 'Baby', 'Babies',
  'Boy', 'Boys', 'Girl', 'Girls', 'Teen', 'Teens', 'Adult', 'Adults',
  'Family', 'Families', 'Parent', 'Parents', 'Father', 'Mother',
  'Dad', 'Mom', 'Daddy', 'Mommy', 'Papa', 'Mama',
  'Son', 'Sons', 'Daughter', 'Daughters', 'Brother', 'Brothers',
  'Sister', 'Sisters', 'Sibling', 'Siblings',
  'Husband', 'Wife', 'Spouse', 'Partner', 'Couple',
  'Friend', 'Friends', 'Neighbor', 'Neighbors',
  'Teacher', 'Student', 'Doctor', 'Nurse', 'Boss', 'Coworker',
  'Guy', 'Guys', 'Dude', 'Folks', 'Everyone', 'Everybody',

  // Body & health
  'Body', 'Bodies', 'Head', 'Face', 'Eye', 'Eyes', 'Ear', 'Ears',
  'Nose', 'Mouth', 'Teeth', 'Tongue', 'Hair', 'Skin',
  'Hand', 'Hands', 'Finger', 'Fingers', 'Arm', 'Arms', 'Leg', 'Legs',
  'Foot', 'Feet', 'Heart', 'Mind', 'Brain', 'Soul', 'Spirit',
  'Health', 'Illness', 'Sickness', 'Disease', 'Pain', 'Ache',

  // Places & spaces
  'Place', 'Places', 'Area', 'Areas', 'Space', 'Spaces',
  'Home', 'Homes', 'House', 'Houses', 'Apartment', 'Room', 'Rooms',
  'Building', 'Buildings', 'Office', 'School', 'Church', 'Store',
  'City', 'Cities', 'Town', 'Towns', 'Village', 'Country', 'Countries',
  'World', 'Earth', 'Land', 'Ground', 'Floor', 'Street', 'Road',
  'Way', 'Ways', 'Path', 'Side', 'Corner', 'Center', 'Middle', 'Edge',
  'Top', 'Bottom', 'Front', 'Back', 'Left', 'Right',

  // Things & objects
  'Thing', 'Things', 'Stuff', 'Object', 'Objects', 'Item', 'Items',
  'Part', 'Parts', 'Piece', 'Pieces', 'Bit', 'Bits',
  'Point', 'Points', 'Line', 'Lines', 'Circle', 'Shape',
  'Book', 'Books', 'Paper', 'Page', 'Pages', 'Letter', 'Letters',
  'Word', 'Words', 'Name', 'Names', 'Number', 'Numbers',
  'Phone', 'Computer', 'Car', 'Cars', 'Door', 'Window',
  'Table', 'Chair', 'Bed', 'Food', 'Water', 'Money',

  // Abstract concepts
  'Life', 'Lives', 'Death', 'Love', 'Hate', 'Fear', 'Fears',
  'Hope', 'Hopes', 'Dream', 'Dreams', 'Wish', 'Wishes',
  'Thought', 'Thoughts', 'Idea', 'Ideas', 'Plan', 'Plans',
  'Feeling', 'Feelings', 'Emotion', 'Emotions', 'Mood', 'Moods',
  'Mind', 'Minds', 'Memory', 'Memories',
  'Reason', 'Reasons', 'Cause', 'Causes', 'Effect', 'Effects',
  'Problem', 'Problems', 'Issue', 'Issues', 'Solution', 'Solutions',
  'Question', 'Questions', 'Answer', 'Answers',
  'Fact', 'Facts', 'Truth', 'Lie', 'Lies', 'Story', 'Stories',
  'Experience', 'Experiences', 'Lesson', 'Lessons',
  'Goal', 'Goals', 'Success', 'Failure', 'Mistake', 'Mistakes',
  'Chance', 'Chances', 'Choice', 'Choices', 'Decision', 'Decisions',
  'Change', 'Changes', 'Difference', 'Differences',
  'Situation', 'Circumstances', 'Condition', 'Conditions',
  'State', 'Level', 'Levels', 'Degree', 'Amount', 'Amounts',
  'Type', 'Types', 'Kind', 'Kinds', 'Sort', 'Sorts',
  'Example', 'Examples', 'Case', 'Cases', 'Instance',
  'Process', 'System', 'Method', 'Approach', 'Technique',
  'Result', 'Results', 'Outcome', 'Outcomes', 'Consequence',
  'Effort', 'Efforts', 'Attempt', 'Attempts', 'Action', 'Actions',
  'Step', 'Steps', 'Stage', 'Stages', 'Phase', 'Progress',
  'Start', 'Beginning', 'End', 'Ending', 'Finish',
  'Future', 'Past', 'Present', 'History',
  'Power', 'Strength', 'Energy', 'Force', 'Pressure',
  'Value', 'Values', 'Worth', 'Meaning', 'Purpose', 'Sense',
  'Interest', 'Attention', 'Focus', 'Care',
  'Help', 'Support', 'Service', 'Advice',
  'Information', 'Knowledge', 'Understanding', 'Awareness',
  'Ability', 'Skill', 'Skills', 'Talent',
  'Opportunity', 'Opportunities', 'Possibility', 'Possibilities',
  'Relationship', 'Relationships', 'Connection', 'Connections',
  'Control', 'Order', 'Chaos', 'Balance',
  'Peace', 'War', 'Conflict', 'Agreement',
  'Rule', 'Rules', 'Law', 'Laws', 'Right', 'Rights',
  'Responsibility', 'Duty', 'Role', 'Roles',

  // Nature & weather
  'Nature', 'Weather', 'Sun', 'Moon', 'Star', 'Stars', 'Sky',
  'Cloud', 'Clouds', 'Rain', 'Snow', 'Wind', 'Storm',
  'Air', 'Fire', 'Water', 'Ice',
  'Tree', 'Trees', 'Plant', 'Plants', 'Flower', 'Flowers',
  'Animal', 'Animals', 'Bird', 'Birds', 'Fish', 'Dog', 'Cat',
  'Ocean', 'Sea', 'River', 'Lake', 'Mountain', 'Hill', 'Valley',

  // Work & activities
  'Work', 'Job', 'Jobs', 'Career', 'Business', 'Company',
  'Project', 'Projects', 'Task', 'Tasks', 'Assignment',
  'Meeting', 'Meetings', 'Event', 'Events', 'Activity', 'Activities',
  'Practice', 'Exercise', 'Game', 'Games', 'Sport', 'Sports',
  'Art', 'Music', 'Movie', 'Movies', 'Show', 'Shows',

  // Numbers & quantifiers
  'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen',
  'Twenty', 'Thirty', 'Forty', 'Fifty', 'Hundred', 'Thousand', 'Million',
  'First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh',
  'Eighth', 'Ninth', 'Tenth', 'Last', 'Next', 'Previous',
  'Half', 'Quarter', 'Double', 'Triple',
  'Few', 'Several', 'Many', 'Much', 'More', 'Most', 'Less', 'Least',
  'Some', 'Any', 'All', 'None', 'Every', 'Each', 'Both', 'Either', 'Neither',
  'Lot', 'Lots', 'Plenty', 'Enough', 'Too',

  // Miscellaneous
  'Yes', 'No', 'Not', 'Okay', 'Ok', 'Sure', 'Well', 'Hello', 'Hi', 'Hey',
  'Bye', 'Goodbye', 'Thanks', 'Thank', 'Please', 'Sorry', 'Excuse',
  'Dear', 'Note', 'Notes', 'Entry', 'Entries', 'Journal', 'Diary',
  'List', 'Lists', 'Update', 'Updates', 'News', 'Report',
  'Review', 'Summary', 'Overview', 'Conclusion', 'Introduction',
]);

/**
 * OCR Context Service
 */
export class OCRContextService {
  // Consolidated collection (migration completed Feb 2026)
  private readonly COLLECTION_NAME = 'userOCRLearning';
  private readonly CACHE_VERSION = '2.0'; // Bumped for Phase 3 consolidation
  private readonly MAX_ENTRIES_TO_ANALYZE = 30;
  private readonly MAX_NAMES = 50;
  private readonly MAX_PLACES = 30;
  private readonly MAX_TERMS = 0; // Disabled - terms are less useful for OCR context than names/places
  private readonly CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours
  private readonly HIGH_CONFIDENCE_ENTRIES = 5; // Names in 5+ entries get 2x boost

  /**
   * Calculate recency weight for an entry based on age
   * - Last 7 days: 1.5x boost
   * - Last 30 days: 1.2x boost
   * - Older: 1.0x (baseline)
   */
  private getRecencyWeight(entryDate: Timestamp | null): number {
    if (!entryDate) return 1.0;

    const ageMs = Date.now() - entryDate.toMillis();
    const ageDays = ageMs / (1000 * 60 * 60 * 24);

    if (ageDays <= 7) return 1.5;   // Last week: 50% boost
    if (ageDays <= 30) return 1.2;  // Last month: 20% boost
    return 1.0;                     // Older: baseline
  }

  /**
   * Get context for a user, refreshing cache if needed
   */
  async getContext(userId: string): Promise<UserOCRContext | null> {
    if (!userId) return null;

    try {
      // Check cache first
      const cached = await this.getCachedContext(userId);

      if (cached && this.isCacheValid(cached)) {
        logger.debug('[OCRContext] Using cached context', {
          userId,
          names: cached.names.length,
          places: cached.places.length,
        });
        return cached;
      }

      // Refresh cache from journal entries
      const context = await this.buildContext(userId);

      if (context) {
        await this.cacheContext(context);
      }

      return context;
    } catch (error) {
      logger.error('[OCRContext] Failed to get context:', error);
      return null;
    }
  }

  /**
   * Build context from user's journal entries
   * Uses recency weighting and cross-entry learning
   */
  private async buildContext(userId: string): Promise<UserOCRContext | null> {
    try {
      const entriesRef = collection(db, COLLECTIONS.JOURNAL_ENTRIES);
      const q = query(
        entriesRef,
        where('uid', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(this.MAX_ENTRIES_TO_ANALYZE)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        logger.debug('[OCRContext] No entries found for user', { userId });
        return null;
      }

      // Track weighted scores and entry counts for cross-entry learning
      const namesMap = new Map<string, { score: number; entries: Set<string> }>();
      const placesMap = new Map<string, { score: number; entries: Set<string> }>();
      const termsMap = new Map<string, number>();

      snapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        const content = data.content || '';
        const entryId = docSnapshot.id;
        const createdAt = data.createdAt as Timestamp | null;

        // Calculate recency weight for this entry
        const recencyWeight = this.getRecencyWeight(createdAt);

        // Extract entities from content with recency weight
        this.extractNames(content, namesMap, recencyWeight, entryId);
        this.extractPlaces(content, placesMap, recencyWeight, entryId);
        this.extractFrequentTerms(content, termsMap, recencyWeight);
      });

      // Sort by frequency (with cross-entry boost) and take top N
      const names = this.getTopEntriesWithCrossEntryBoost(namesMap, this.MAX_NAMES);
      const places = this.getTopEntriesWithCrossEntryBoost(placesMap, this.MAX_PLACES);
      const terms = this.getTopTerms(termsMap, this.MAX_TERMS);

      const context: UserOCRContext = {
        userId,
        names,
        places,
        terms,
        lastUpdated: Timestamp.now(),
        entryCount: snapshot.size,
        version: this.CACHE_VERSION,
      };

      logger.info('[OCRContext] Built context from entries', {
        userId,
        entryCount: snapshot.size,
        names: names.length,
        places: places.length,
        terms: terms.length,
      });

      return context;
    } catch (error) {
      logger.error('[OCRContext] Failed to build context:', error);
      return null;
    }
  }

  /**
   * Extract potential names from text with recency weight and entry tracking
   */
  private extractNames(
    text: string,
    namesMap: Map<string, { score: number; entries: Set<string> }>,
    weight: number,
    entryId: string
  ): void {
    // Extract names with prefixes (e.g., "Dr. Smith", "Grandma Rose")
    let match;
    const prefixRegex = new RegExp(NAME_PATTERNS.prefixes.source, 'g');
    while ((match = prefixRegex.exec(text)) !== null) {
      const name = match[2];
      if (name && !EXCLUDE_WORDS.has(name)) {
        const existing = namesMap.get(name) || { score: 0, entries: new Set<string>() };
        existing.score += 2 * weight; // Higher weight for prefixed names
        existing.entries.add(entryId);
        namesMap.set(name, existing);
      }
    }

    // Extract standalone capitalized words (potential names)
    const words = text.match(NAME_PATTERNS.capitalizedWords) || [];
    for (const word of words) {
      if (word.length >= 3 && !EXCLUDE_WORDS.has(word)) {
        const existing = namesMap.get(word) || { score: 0, entries: new Set<string>() };
        existing.score += 1 * weight;
        existing.entries.add(entryId);
        namesMap.set(word, existing);
      }
    }
  }

  /**
   * Extract potential places from text with recency weight and entry tracking
   * Uses strict filtering to only capture actual geographic locations
   */
  private extractPlaces(
    text: string,
    placesMap: Map<string, { score: number; entries: Set<string> }>,
    weight: number,
    entryId: string
  ): void {
    let match;
    const placeRegex = new RegExp(NAME_PATTERNS.placeIndicators.source, 'gi');
    while ((match = placeRegex.exec(text)) !== null) {
      const place = match[1]; // Changed from match[2] due to regex update
      if (!place || place.length < 3) continue;

      // Get the first word to check against exclusions
      const firstWord = place.split(/\s+/)[0];

      // Skip if first word is in exclusion lists
      if (EXCLUDE_WORDS.has(firstWord)) continue;
      if (PLACE_EXCLUDE_WORDS.has(firstWord)) continue;

      // Check if it looks like an actual place
      const words = place.split(/\s+/);
      const lastWord = words[words.length - 1];
      const hasPlaceSuffix = PLACE_SUFFIXES.has(lastWord);
      const isMultiWord = words.length >= 2;
      const hasAllCaps = words.some(w => w === w.toUpperCase() && w.length > 1); // e.g., "NYC"

      // Only accept if it has a place suffix, is multi-word, or all words are capitalized properly
      // This filters out single common words like "Shine" that follow prepositions
      if (!hasPlaceSuffix && !isMultiWord && !hasAllCaps) {
        // Single word without place suffix - only accept if it's clearly a proper noun
        // (not in any exclusion list and appears capitalized consistently)
        if (firstWord.length < 4) continue;
      }

      // Score boost for places with recognizable suffixes
      const scoreMultiplier = hasPlaceSuffix ? 2 : 1;

      const existing = placesMap.get(place) || { score: 0, entries: new Set<string>() };
      existing.score += scoreMultiplier * weight;
      existing.entries.add(entryId);
      placesMap.set(place, existing);
    }
  }

  private extractFrequentTerms(text: string, termsMap: Map<string, number>, weight: number): void {
    // Skip term extraction if disabled (MAX_TERMS = 0)
    if (this.MAX_TERMS === 0) return;

    // Tokenize and count words - require 5+ letters for more meaningful terms
    const words = text.toLowerCase().match(/\b[a-z]{5,}\b/g) || [];

    for (const word of words) {
      // Skip very common words (check capitalized version against EXCLUDE_WORDS)
      if (EXCLUDE_WORDS.has(word.charAt(0).toUpperCase() + word.slice(1))) continue;

      // Skip common English words that aren't useful for OCR context
      if (COMMON_TERM_EXCLUSIONS.has(word)) continue;

      termsMap.set(word, (termsMap.get(word) || 0) + weight);
    }
  }

  /**
   * Get top N entries with cross-entry boost (2x for items in 5+ entries)
   */
  private getTopEntriesWithCrossEntryBoost(
    map: Map<string, { score: number; entries: Set<string> }>,
    maxCount: number
  ): string[] {
    return Array.from(map.entries())
      .filter(([, data]) => data.score >= 2) // Must have meaningful score
      .map(([word, data]) => ({
        word,
        // Apply 2x boost for items appearing in 5+ entries
        finalScore: data.entries.size >= this.HIGH_CONFIDENCE_ENTRIES
          ? data.score * 2
          : data.score
      }))
      .sort((a, b) => b.finalScore - a.finalScore)
      .slice(0, maxCount)
      .map(item => item.word);
  }

  /**
   * Get top N terms from a frequency map
   */
  private getTopTerms(map: Map<string, number>, maxCount: number): string[] {
    return Array.from(map.entries())
      .filter(([, count]) => count >= 2) // Must appear at least twice
      .sort((a, b) => b[1] - a[1])
      .slice(0, maxCount)
      .map(([word]) => word);
  }

  /**
   * Get cached context from Firestore
   * Phase 3: Reads from consolidated collection first, falls back to legacy
   */
  private async getCachedContext(userId: string): Promise<UserOCRContext | null> {
    try {
      // Try consolidated collection first
      const consolidatedRef = doc(db, this.COLLECTION_NAME, userId);
      const consolidatedSnap = await getDoc(consolidatedRef);

      if (consolidatedSnap.exists()) {
        const data = consolidatedSnap.data();
        // Handle both old flat structure and new nested structure
        if (data.context) {
          // New consolidated structure
          return {
            userId: data.userId,
            names: data.context.names || [],
            places: data.context.places || [],
            terms: data.context.terms || [],
            lastUpdated: data.lastUpdated,
            entryCount: data.context.entryCount || 0,
            version: data.context.version || this.CACHE_VERSION,
          };
        }
        // Old structure in new collection
        return data as UserOCRContext;
      }

      return null;
    } catch (error) {
      logger.warn('[OCRContext] Failed to get cached context:', error);
      return null;
    }
  }

  /**
   * Cache context in Firestore (consolidated userOCRLearning collection)
   */
  private async cacheContext(context: UserOCRContext): Promise<void> {
    try {
      const now = Timestamp.now();

      // Write to consolidated collection with nested structure
      const consolidatedRef = doc(db, this.COLLECTION_NAME, context.userId);
      await setDoc(consolidatedRef, {
        userId: context.userId,
        context: {
          names: context.names,
          places: context.places,
          terms: context.terms,
          entryCount: context.entryCount,
          version: context.version,
        },
        lastUpdated: now,
      }, { merge: true });

      logger.debug('[OCRContext] Cached context', { userId: context.userId });
    } catch (error) {
      logger.warn('[OCRContext] Failed to cache context:', error);
    }
  }

  /**
   * Check if cached context is still valid
   */
  private isCacheValid(context: UserOCRContext): boolean {
    if (context.version !== this.CACHE_VERSION) return false;

    if (!context.lastUpdated) return false;

    const lastUpdatedMs = context.lastUpdated.toMillis();
    const ageMs = Date.now() - lastUpdatedMs;

    return ageMs < this.CACHE_DURATION_MS;
  }

  /**
   * Format context for inclusion in OCR request
   */
  formatForOCR(context: UserOCRContext | null): string | null {
    if (!context) return null;

    const parts: string[] = [];

    if (context.names.length > 0) {
      parts.push(`Names: ${context.names.slice(0, 15).join(', ')}`);
    }

    if (context.places.length > 0) {
      parts.push(`Places: ${context.places.slice(0, 10).join(', ')}`);
    }

    if (parts.length === 0) return null;

    return parts.join('\n');
  }

  /**
   * Incorporate user corrections into cached context
   * This enables unified learning - corrections from Word Review
   * immediately improve future OCR results
   */
  async incorporateCorrections(userId: string, correctedWords: string[]): Promise<void> {
    if (!userId || correctedWords.length === 0) return;

    try {
      // Get current cached context
      const cached = await this.getCachedContext(userId);
      if (!cached) {
        logger.debug('[OCRContext] No cached context to update with corrections', { userId });
        return;
      }

      // Filter to potential proper nouns (capitalized, 3+ chars, not already in list)
      const newNames = correctedWords.filter(word =>
        word.length >= 3 &&
        /^[A-Z]/.test(word) &&
        !EXCLUDE_WORDS.has(word) &&
        !cached.names.includes(word)
      );

      if (newNames.length === 0) {
        logger.debug('[OCRContext] No new names to add from corrections', { userId });
        return;
      }

      // Add new names at the front (highest priority) and trim to max
      cached.names = [...newNames, ...cached.names].slice(0, this.MAX_NAMES);
      cached.lastUpdated = Timestamp.now();

      // Save updated context
      await this.cacheContext(cached);

      logger.info('[OCRContext] Incorporated corrections into context', {
        userId,
        added: newNames,
        totalNames: cached.names.length
      });
    } catch (error) {
      logger.warn('[OCRContext] Failed to incorporate corrections:', error);
    }
  }

  /**
   * Invalidate cache for a user (call when new entries are added)
   */
  async invalidateCache(userId: string): Promise<void> {
    try {
      const consolidatedRef = doc(db, this.COLLECTION_NAME, userId);
      await setDoc(consolidatedRef, {
        context: { version: 'invalidated' },
        lastUpdated: Timestamp.now()
      }, { merge: true });

      logger.debug('[OCRContext] Invalidated cache', { userId });
    } catch (error) {
      logger.warn('[OCRContext] Failed to invalidate cache:', error);
    }
  }
}

// Export singleton instance
export const ocrContextService = new OCRContextService();
export default ocrContextService;
